/**
 * MultiScatterLut — Hillaire 2020 multiple-scattering approximation LUT.
 *
 * Single-scattering alone makes the sky look too dark and "thin", especially
 * near the horizon and in dense (Mie-heavy) atmospheres. Hillaire §5
 * introduces a cheap, energy-conserving 2nd-order term that approximates the
 * INFINITE multiple-scattering series by assuming the multiply-scattered
 * radiance is locally isotropic and uniform (so the phase functions integrate
 * to 1 and a geometric series closes the higher orders).
 *
 * Parameterization (32×32), Hillaire §5.4:
 *   uv.x ← (cosSunZenith + 1) / 2      (sun zenith cosine, full −1..1 range)
 *   uv.y ← (r − Rg) / (Rt − Rg)        (normalised altitude)
 *
 * Per texel we evaluate, over `dirCount` uniformly distributed sphere
 * directions (Hillaire uses 8×8 = 64 in UE; the isotropic assumption makes
 * even 8–16 directions converge well), two quantities accumulated along each
 * direction's ray-march:
 *
 *   L_2nd      — 2nd-order in-scattered radiance assuming unit incident light
 *   f_ms       — fraction of light that gets scattered back into the medium
 *                (the "multiple-scattering transfer" integral)
 *
 * The closed-form infinite series (eq. 10 in the paper):
 *
 *   F_ms      = 1 / (1 − f_ms)                       (geometric sum)
 *   Ψ_ms      = L_2nd · F_ms
 *
 * Ψ_ms is stored in RGB. It is added (× sun illuminance × per-channel
 * scattering) as an isotropic ambient term inside the sky-view march.
 *
 * Reference:
 *   Hillaire 2020, §5 + eqs. (5)–(10). UE SkyAtmosphere MultiScattLutCS.
 */

import type * as THREE from 'three/webgpu';
import type { AtmosphereConfig, Vec3 } from '../AtmosphereConfig.js';
import { computeDensities } from '../AtmosphereConfig.js';
import { rayAtmosphereIntersect, transmittanceUV } from '../lutParameterization.js';
import {
  makeFloatTexture, sampleTransmittanceLut, clamp01, rayNearIntersect,
} from './TransmittanceLut.js';

/** Canonical Hillaire multi-scatter LUT size. */
export const MULTISCATTER_LUT_SIZE = 32;

/** Ray-march steps per direction inside the multi-scatter integral. */
export const MULTISCATTER_MARCH_STEPS = 20;

/** Uniformly distributed sphere directions (Hillaire's "isotropic" sampling). */
export const MULTISCATTER_DIR_COUNT = 16;

/** Map (cosSunZenith, altitude) → multi-scatter LUT UV. */
export function multiScatterUV(
  cfg: AtmosphereConfig, radius: number, cosSunZenith: number,
): { u: number; v: number } {
  const u = clamp01(cosSunZenith * 0.5 + 0.5);
  const v = clamp01((radius - cfg.bottomRadius) / (cfg.topRadius - cfg.bottomRadius));
  return { u, v };
}

/** Inverse: multi-scatter LUT UV → (radius, cosSunZenith). */
export function multiScatterUVInverse(
  cfg: AtmosphereConfig, u: number, v: number,
): { radius: number; cosSunZenith: number } {
  const cosSunZenith = clamp01(u) * 2.0 - 1.0;
  const radius = cfg.bottomRadius + clamp01(v) * (cfg.topRadius - cfg.bottomRadius);
  return { radius, cosSunZenith };
}

/**
 * Generate `n` (near-)uniformly distributed unit directions on the sphere via
 * the Fibonacci spiral. Deterministic — used identically on the CPU bake.
 */
export function uniformSphereDirections(n: number): Vec3[] {
  const dirs: Vec3[] = [];
  const golden = Math.PI * (3.0 - Math.sqrt(5.0)); // ≈2.39996 rad
  for (let i = 0; i < n; i++) {
    const z = 1.0 - (2.0 * i + 1.0) / n;       // z ∈ (−1, 1), centred
    const r = Math.sqrt(Math.max(0, 1.0 - z * z));
    const phi = i * golden;
    dirs.push({ x: r * Math.cos(phi), y: z, z: r * Math.sin(phi) });
  }
  return dirs;
}

export interface MultiScatterContext {
  cfg: AtmosphereConfig;
  /** Baked transmittance LUT (Float RGBA). */
  transmittanceData: Float32Array;
  transmittanceWidth: number;
  transmittanceHeight: number;
}

/**
 * CPU reference: evaluate Hillaire's Ψ_ms at (radius, cosSunZenith).
 *
 * For each of `dirCount` uniform sphere directions we march `steps`
 * segments, accumulating:
 *   - L2  : 2nd-order in-scattered radiance (uses single-scattering of the
 *           sun reaching each sample, isotropic phase 1/4π folded in).
 *   - fms : the medium's scattering-back fraction.
 * Then F = 1/(1 − fms), Ψ = L2 · F.
 *
 * The sun is in the local +up frame with zenith cosine `cosSunZenith`; the
 * flat-frame assumption keeps that cosine constant along the short path, so
 * the transmittance LUT lookup uses it directly at each sample radius.
 */
export function evaluateMultiScatter(
  ctx: MultiScatterContext,
  radius: number,
  cosSunZenith: number,
  dirCount = MULTISCATTER_DIR_COUNT,
  steps = MULTISCATTER_MARCH_STEPS,
): Vec3 {
  const { cfg } = ctx;
  const dirs = uniformSphereDirections(dirCount);
  // Accumulate L2 (RGB) and fms (RGB) averaged over the sphere.
  let l2x = 0, l2y = 0, l2z = 0;
  let fmsx = 0, fmsy = 0, fmsz = 0;
  // Solid-angle weight per direction for the sphere average (∫dΩ = 4π,
  // isotropic so each dir carries 4π/n; the 1/4π isotropic phase cancels it,
  // leaving a simple 1/n average — Hillaire's "uniformPhase" simplification).
  const wPerDir = 1.0 / dirCount;

  for (const dir of dirs) {
    const muDir = dir.y; // cosine of this direction with local up
    const tMax = rayAtmosphereIntersect(radius, muDir, cfg.topRadius);
    // Clamp against the ground: a downward ray stops at the NEAREST surface
    // intersection (not the far root, which would pass through the planet).
    const tGround = rayNearIntersect(radius, muDir, cfg.bottomRadius);
    const tEnd = (tGround > 0) ? Math.min(tMax, tGround) : tMax;
    if (tEnd <= 0) continue;

    const dt = tEnd / steps;
    // Throughput (transmittance) along THIS direction's ray.
    let trX = 1, trY = 1, trZ = 1;

    for (let s = 0; s < steps; s++) {
      const t = (s + 0.5) * dt;
      const rAtSample = Math.sqrt(radius * radius + 2.0 * radius * muDir * t + t * t);
      const altitudeKm = rAtSample - cfg.bottomRadius;
      const d = computeDensities(cfg, altitudeKm);

      // Scattering coefficients at the sample (σ_s = Rayleigh + Mie scatter).
      const sigSx = cfg.rayleighScattering.x * d.rayleigh + cfg.mieScattering.x * d.mie;
      const sigSy = cfg.rayleighScattering.y * d.rayleigh + cfg.mieScattering.y * d.mie;
      const sigSz = cfg.rayleighScattering.z * d.rayleigh + cfg.mieScattering.z * d.mie;

      // Total extinction σ_t = Rayleigh scatter + Mie extinction + ozone.
      const extX = cfg.rayleighScattering.x * d.rayleigh
        + cfg.mieExtinction.x * d.mie + cfg.ozoneExtinction.x * d.ozone;
      const extY = cfg.rayleighScattering.y * d.rayleigh
        + cfg.mieExtinction.y * d.mie + cfg.ozoneExtinction.y * d.ozone;
      const extZ = cfg.rayleighScattering.z * d.rayleigh
        + cfg.mieExtinction.z * d.mie + cfg.ozoneExtinction.z * d.ozone;

      // Per-segment transmittance for THIS direction's throughput.
      const segTx = Math.exp(-extX * dt);
      const segTy = Math.exp(-extY * dt);
      const segTz = Math.exp(-extZ * dt);

      // Sun transmittance from this sample to the top of atmosphere via the
      // transmittance LUT (cosSunZenith stays constant for a directional sun
      // in the local frame — the standard Hillaire flat-frame assumption).
      const tUV = transmittanceUV(cfg, rAtSample, cosSunZenith);
      const sunT = sampleTransmittanceLut(
        ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight,
        tUV.u, tUV.v,
      );

      // 2nd-order in-scatter (uniform phase 1/4π folded into wPerDir average):
      //   L2 += throughput · σ_s · sunT · dt
      l2x += trX * sigSx * sunT.x * dt * wPerDir;
      l2y += trY * sigSy * sunT.y * dt * wPerDir;
      l2z += trZ * sigSz * sunT.z * dt * wPerDir;

      // fms: fraction scattered (without the sun term) — the medium's
      //   "albedo×density" integral that feeds the geometric series.
      fmsx += trX * sigSx * dt * wPerDir;
      fmsy += trY * sigSy * dt * wPerDir;
      fmsz += trZ * sigSz * dt * wPerDir;

      trX *= segTx; trY *= segTy; trZ *= segTz;
    }
  }

  // Geometric series closure: F = 1/(1 − fms); guard fms < 1.
  const fx = 1.0 / Math.max(1e-4, 1.0 - Math.min(fmsx, 0.999));
  const fy = 1.0 / Math.max(1e-4, 1.0 - Math.min(fmsy, 0.999));
  const fz = 1.0 / Math.max(1e-4, 1.0 - Math.min(fmsz, 0.999));
  return { x: l2x * fx, y: l2y * fy, z: l2z * fz };
}

export interface MultiScatterLutResult {
  data: Float32Array;
  width: number;
  height: number;
  texture: THREE.DataTexture;
}

/** Bake the multi-scatter LUT (32×32 Float RGBA) from a transmittance LUT. */
export function bakeMultiScatterLut(
  ctx: MultiScatterContext,
  size = MULTISCATTER_LUT_SIZE,
  dirCount = MULTISCATTER_DIR_COUNT,
  steps = MULTISCATTER_MARCH_STEPS,
): MultiScatterLutResult {
  const data = new Float32Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;
      const { radius, cosSunZenith } = multiScatterUVInverse(ctx.cfg, u, v);
      const psi = evaluateMultiScatter(ctx, radius, cosSunZenith, dirCount, steps);
      const i = (y * size + x) * 4;
      data[i + 0] = psi.x;
      data[i + 1] = psi.y;
      data[i + 2] = psi.z;
      data[i + 3] = 1.0;
    }
  }
  const texture = makeFloatTexture(data, size, size, 'SkyMultiScatterLUT');
  return { data, width: size, height: size, texture };
}

/** Bilinear sample of a baked multi-scatter LUT (mirrors GPU sampling). */
export function sampleMultiScatterLut(
  data: Float32Array, size: number, u: number, v: number,
): Vec3 {
  return sampleTransmittanceLut(data, size, size, u, v);
}
